import React, {
  useEffect,
  useState,
} from "react";

import {
  FaListOl,
} from "react-icons/fa";

import { toast } from "react-toastify";

import EditorDrawerShell from "./EditorDrawerShell";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-4 focus:ring-violet-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-violet-900/40";

const INITIAL_FORM = {
  slug: "",
  titre: "",
  contenu: "",
  format_contenu: "texte",
  mots_cles: "",
  roles_cibles: "",
  bouton_label: "",
  bouton_url: "",
  bouton_nouvel_onglet: false,
  ordre: 10,
  is_active: true,
};

function rolesToString(roles) {
  if (!roles) {
    return "";
  }

  if (Array.isArray(roles)) {
    return roles.join(", ");
  }

  if (typeof roles === "string") {
    try {
      const parsed = JSON.parse(roles);

      return Array.isArray(parsed)
        ? parsed.join(", ")
        : roles;
    } catch {
      return roles;
    }
  }

  return "";
}

export default function TutorialStepDrawer({
  drawer,
  saving,
  onClose,
  onSave,
}) {
  const isOpen =
    drawer.open &&
    drawer.type === "step";

  const [formData, setFormData] =
    useState(INITIAL_FORM);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (
      drawer.mode === "edit" &&
      drawer.data
    ) {
      setFormData({
        slug: drawer.data.slug || "",
        titre: drawer.data.titre || "",
        contenu:
          drawer.data.contenu || "",

        format_contenu:
          drawer.data.format_contenu ||
          "texte",

        mots_cles:
          drawer.data.mots_cles || "",

        roles_cibles: rolesToString(
          drawer.data.roles_cibles
        ),

        bouton_label:
          drawer.data.bouton_label || "",

        bouton_url:
          drawer.data.bouton_url || "",

        bouton_nouvel_onglet:
          drawer.data
            .bouton_nouvel_onglet === true,

        ordre: drawer.data.ordre || 10,

        is_active:
          drawer.data.is_active !== false,
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [isOpen, drawer]);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((current) => ({
      ...current,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.slug.trim()) {
      toast.error(
        "Le slug de l’étape est obligatoire."
      );
      return;
    }

    if (!formData.titre.trim()) {
      toast.error(
        "Le titre de l’étape est obligatoire."
      );
      return;
    }

    if (!formData.contenu.trim()) {
      toast.error(
        "Le contenu de l’étape est obligatoire."
      );
      return;
    }

    const roles = formData.roles_cibles
      .split(",")
      .map((role) =>
        role.trim().toLowerCase()
      )
      .filter(Boolean);

    await onSave({
      slug: formData.slug
        .trim()
        .toLowerCase(),

      titre: formData.titre.trim(),

      contenu:
        formData.contenu.trim(),

      format_contenu:
        formData.format_contenu,

      mots_cles:
        formData.mots_cles.trim() ||
        null,

      roles_cibles:
        roles.length > 0 ? roles : null,

      bouton_label:
        formData.bouton_label.trim() ||
        null,

      bouton_url:
        formData.bouton_url.trim() ||
        null,

      bouton_nouvel_onglet:
        formData.bouton_nouvel_onglet,

      ordre:
        Number(formData.ordre) || 0,

      is_active: formData.is_active,
    });
  };

  return (
    <EditorDrawerShell
      open={isOpen}
      eyebrow={
        drawer.mode === "edit"
          ? "Modification"
          : "Nouvelle étape"
      }
      title={
        drawer.mode === "edit"
          ? "Modifier l’étape"
          : "Ajouter une étape"
      }
      description="Chaque étape contient un titre, un texte explicatif et éventuellement un bouton ou plusieurs médias."
      icon={<FaListOl />}
      saving={saving}
      submitLabel={
        drawer.mode === "edit"
          ? "Enregistrer"
          : "Créer l’étape"
      }
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <FormSection
          title="Contenu principal"
          description="Texte affiché dans la carte du tutoriel."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label required>Slug</Label>

              <input
                className={INPUT}
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="inscription-adherent"
              />
            </Field>

            <Field>
              <Label>Format</Label>

              <select
                className={INPUT}
                name="format_contenu"
                value={
                  formData.format_contenu
                }
                onChange={handleChange}
              >
                <option value="texte">
                  Texte simple
                </option>

                <option value="markdown">
                  Markdown
                </option>

                <option value="html">
                  HTML nettoyé
                </option>
              </select>
            </Field>

            <Field className="md:col-span-2">
              <Label required>Titre</Label>

              <input
                className={INPUT}
                name="titre"
                value={formData.titre}
                onChange={handleChange}
              />
            </Field>

            <Field className="md:col-span-2">
              <Label required>Contenu</Label>

              <textarea
                className={`${INPUT} min-h-[280px] resize-y leading-7`}
                name="contenu"
                value={formData.contenu}
                onChange={handleChange}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Recherche et visibilité"
          description="Mots-clés, rôles ciblés et ordre d’affichage."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label>Mots-clés</Label>

              <input
                className={INPUT}
                name="mots_cles"
                value={formData.mots_cles}
                onChange={handleChange}
                placeholder="inscription, adhérent, formulaire"
              />
            </Field>

            <Field>
              <Label>Rôles ciblés</Label>

              <input
                className={INPUT}
                name="roles_cibles"
                value={
                  formData.roles_cibles
                }
                onChange={handleChange}
                placeholder="utilisateur, enseignant"
              />
            </Field>

            <Field>
              <Label>Ordre</Label>

              <input
                type="number"
                min="0"
                step="10"
                className={INPUT}
                name="ordre"
                value={formData.ordre}
                onChange={handleChange}
              />
            </Field>
          </div>

          <div className="mt-5">
            <ToggleCard
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              title="Étape active"
              description="Autorise son affichage dans le tutoriel public."
            />
          </div>
        </FormSection>

        <FormSection
          title="Bouton optionnel"
          description="Permet d’ajouter un lien d’action sous le contenu."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label>Libellé</Label>

              <input
                className={INPUT}
                name="bouton_label"
                value={
                  formData.bouton_label
                }
                onChange={handleChange}
                placeholder="Accéder à la page"
              />
            </Field>

            <Field>
              <Label>URL</Label>

              <input
                className={INPUT}
                name="bouton_url"
                value={formData.bouton_url}
                onChange={handleChange}
                placeholder="/inscription"
              />
            </Field>
          </div>

          <div className="mt-5">
            <ToggleCard
              name="bouton_nouvel_onglet"
              checked={
                formData.bouton_nouvel_onglet
              }
              onChange={handleChange}
              title="Ouvrir dans un nouvel onglet"
              description="Recommandé pour un lien externe."
            />
          </div>
        </FormSection>
      </div>
    </EditorDrawerShell>
  );
}

function FormSection({
  title,
  description,
  children,
}) {
  return (
    <section className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h3>

      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {description}
      </p>

      <div className="mt-5">
        {children}
      </div>
    </section>
  );
}

function Field({
  children,
  className = "",
}) {
  return (
    <div className={className}>
      {children}
    </div>
  );
}

function Label({
  children,
  required = false,
}) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}

      {required ? (
        <span className="ml-1 text-rose-500">
          *
        </span>
      ) : null}
    </label>
  );
}

function ToggleCard({
  name,
  checked,
  onChange,
  title,
  description,
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
      <div>
        <div className="font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </div>

        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {description}
        </div>
      </div>

      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="h-5 w-5 rounded border-slate-300 text-violet-600"
      />
    </label>
  );
}